import React, { useEffect, useState } from "react";

import { FormError, FormMixPanel, FormValue } from "@types";
import { handleOnBlurLiveMixpanel } from "../../../../utils/mixpanelEvents/mixpanelSignupFormEvent";
import {
  LivePromiseType,
  liveValidation,
} from "../../helpers/validations/liveValidation";
import { validateLiveOne } from "../../helpers/validations/validateLiveOne";

import SignUpLoading from "../../../Loadings/SignUpLoading/SignUpLoading";
import { isValidEmail } from "../../../../utils/validations";
import {
  handleOnBlurIncubeta,
  SignUpFormFields,
} from "../../../../utils/incubetaEvents/signupFormEvent";

type EmailPropsType = {
  email: FormValue & FormMixPanel;
  emailError: FormError;
  emailValidationClass: string;
  setEmail: React.Dispatch<React.SetStateAction<FormValue & FormMixPanel>>;
  setEmailError: React.Dispatch<React.SetStateAction<FormError>>;
  styles: { [key: string]: any };
  submitedUserEmail?: string;
};

export const EmailContainer: React.FC<EmailPropsType> = ({
  email,
  emailError,
  emailValidationClass,
  setEmail,
  setEmailError,
  styles,
  submitedUserEmail,
}) => {
  // email state
  const [emailKeyupTimeout, setEmailKeyupTimeout] = useState(null);
  const [emailIsLoading, setEmailIsLoading] = useState(false);
  const [mCheck, setMCheck] = useState({
    shouldShow: false,
    suggestion: "",
  });

  useEffect(() => {
    handleSetEmailOnComponentMounted();

    return () => {
      setEmailKeyupTimeout(null);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetEmailOnComponentMounted = () => {
    if (!submitedUserEmail) return;

    const newValues = { ...email };
    newValues.value = submitedUserEmail;
    if (!isValidEmail(newValues.value)) {
      setEmail({
        ...newValues,
      });
      setEmailError({
        validation: {
          hasError: true,
          message: "Please enter a valid email address.",
        },
      });
    } else {
      setEmail(newValues);
    }
  };

  const handleKeypressLive = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const { name } = e.currentTarget;

    if (name === "email" && !email.isUpdated) {
      setEmail(prevState => {
        const updatedState = {
          ...prevState,
          isUpdated: true,
        };

        return updatedState;
      });
    }
  };

  const onBlurLiveValHandler = e => {
    const { name } = e.target;

    if (name === "email") {
      validateLiveOne(e, setEmailError, error => {
        if (!isValidEmail(e.target.value)) {
          handleOnBlurLiveMixpanel({
            e,
            formData: email,
            formDataError: { validation: error },
            setFormData: setEmail,
            setFormDataError: setEmailError,
          });
        } else {
          const formData: SignUpFormFields = {
            [name]: { validation: { hasError: false, message: "sucess" } },
          };
          handleOnBlurIncubeta({ e, formData });
        }
      });
    }
  };

  const handleLiveValChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "email") {
      setMCheck({
        shouldShow: false,
        suggestion: "",
      });

      setEmail(prevState => ({ ...prevState, value }));

      setEmailError({
        validation: {
          hasError: false,
          message: "",
        },
      });
    }
  };

  const keyUpHandler = async function (e) {
    const { name, value } = e.target;
    if (name === "email" && isValidEmail(value)) {
      await timeOutHandler(
        e,
        setEmailError,
        emailKeyupTimeout,
        setEmailKeyupTimeout,
        setEmailIsLoading
      );
    }
  };

  const timeOutHandler = async (
    e,
    setInputError,
    inputTimeout,
    setInputTimeout,
    setIsLoading
  ) => {
    if (inputTimeout !== null) {
      await clearTimeout(inputTimeout);
      await setInputTimeout(null);
    }
    setInputTimeout(
      setTimeout(() => {
        liveValidation(e, setInputError, setIsLoading, setMCheck)
          .then(handleLiveMxpCallbacks)
          .catch(handleLiveMxpCallbacks);
      }, 1000)
    );
  };

  const handleLiveMxpCallbacks = (data: LivePromiseType) => {
    if (data.name === "email") {
      handleOnBlurLiveMixpanel({
        e: data.e,
        formData: email,
        formDataError: { validation: data.validation },
        setFormData: setEmail,
        setFormDataError: setEmailError,
      });
    }
  };

  const handleOnFocusLive = (e: React.FocusEvent<HTMLInputElement>) => {
    if ("target" in e) {
      const { name } = e["target"];
      if (name === "email") {
        setEmail(prevState => {
          const updatedState = { ...prevState };
          updatedState.timeIn = Date.now();

          return updatedState;
        });
      }
    }
  };

  const onClickSuggestion = e => {
    const mockEvtData = {
      target: { name: "email", value: e.target.innerText },
    };
    const mockEmailObj = { ...email };
    mockEmailObj.value = mockEvtData.target.value;
    setEmail({
      ...mockEmailObj,
    });
    setMCheck({
      ...mCheck,
      shouldShow: false,
      suggestion: "",
    });
    setTimeout(() => {
      liveValidation(mockEvtData, setEmailError, setEmailIsLoading, setMCheck)
        .then(handleLiveMxpCallbacks)
        .catch(handleLiveMxpCallbacks);
    }, 1000);
  };

  return (
    <>
      <label htmlFor="email">Email</label>
      <div className={styles.inputContainer}>
        <input
          data-testid="signup-form-email"
          className={`${emailValidationClass} ${styles.inputMargin}`}
          type="email"
          value={email.value}
          name="email"
          onChange={handleLiveValChange}
          onBlur={onBlurLiveValHandler}
          onKeyUp={keyUpHandler}
          onFocus={handleOnFocusLive}
          onKeyPress={handleKeypressLive}
        />
        {emailIsLoading && <SignUpLoading />}
        {emailError.validation.hasError && <div className={styles.errorIcon} />}
      </div>

      {mCheck.shouldShow && (
        <p className={styles.mCheckSuggestion}>
          Did you mean{" "}
          <span onClick={onClickSuggestion}>{mCheck.suggestion}</span>?
        </p>
      )}
      {!mCheck.shouldShow && emailError.validation.hasError && (
        <p className={styles.activeError}>{emailError.validation.message}</p>
      )}
    </>
  );
};

export default EmailContainer;
